import { useCallback, useEffect, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { EndorsementError } from 'src/app/pages/starterDashboardEndorsements/shared/hooks';
import { useEndorsementForm } from 'src/app/pages/starterDashboardEndorsements/shared/hooks';
import { normalizeUrlForHref } from 'src/app/shared/components/endorsements';
import type { EndorsementFormData } from 'src/app/shared/components/endorsements';

import { updateEndorsement } from '../../../api';
import { getErrorType, getPhotoUrl } from '../../../shared/helpers';
import { useEndorsementImageUpload } from '../useEndorsementImageUpload';

export type { EndorsementError } from 'src/app/pages/starterDashboardEndorsements/shared/hooks';

type UseEditEndorsementProps = {
	endorsementId: string;
	petitionId: string;
	initialData?: Partial<EndorsementFormData>;
	onSuccess?: () => void;
};

type TrackFn = (event: string, data?: Record<string, unknown>) => Promise<boolean>;
type SetErrorFn = (error: EndorsementError | null) => void;

function handleSubmitError(track: TrackFn, setError: SetErrorFn, msg: string, type: EndorsementError['type']) {
	void track('starter_dashboard_endorsement_update_error', { error: msg });
	setError({ message: msg, type });
}

function buildUpdateInput(
	endorsementId: string,
	petitionId: string,
	formData: EndorsementFormData,
	photo?: { url: string; isNewUpload: boolean },
) {
	return {
		endorsementId,
		petitionId,
		name: formData.name,
		title: formData.title,
		url: formData.url ? normalizeUrlForHref(formData.url) : undefined,
		content: formData.statement,
		// Only include photo if it's a new upload
		...(photo?.url && photo.isNewUpload && { photo: { url: photo.url } }),
	};
}

// Sync image from upload context to form data
function useSyncImageToForm(image: string | undefined, handlePhotoUpload: (url: string) => void) {
	useEffect(() => {
		if (image) handlePhotoUpload(image);
	}, [image, handlePhotoUpload]);
}

export function useEditEndorsement({ endorsementId, petitionId, initialData, onSuccess }: UseEditEndorsementProps) {
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const form = useEndorsementForm(initialData);
	const imageUpload = useEndorsementImageUpload();
	const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
	const [isTermsAgreed, setIsTermsAgreed] = useState(false);

	useSyncImageToForm(imageUpload.data.image, form.handlePhotoUpload);

	const handleSubmit = useCallback(async () => {
		form.setIsSubmitting(true);
		form.setError(null);
		try {
			void track('starter_dashboard_endorsement_edit_submit_click');
			const uploadResult = await getPhotoUrl(imageUpload.data.image, imageUpload.uploader);
			if (uploadResult && !uploadResult.success) {
				handleSubmitError(track, form.setError, uploadResult.error, 'SERVER_ERROR');
				return;
			}
			const input = buildUpdateInput(
				endorsementId,
				petitionId,
				form.formData,
				uploadResult?.url && uploadResult.isNewUpload
					? { url: uploadResult.url, isNewUpload: uploadResult.isNewUpload }
					: undefined,
			);
			const response = await updateEndorsement(input, utilityContext);
			if (response.success) {
				void track('starter_dashboard_endorsement_update_success');
				onSuccess?.();
			} else {
				const msg = response.errorMessage || 'Failed to update endorsement';
				handleSubmitError(track, form.setError, msg, getErrorType(msg));
			}
		} catch (e) {
			handleSubmitError(track, form.setError, e instanceof Error ? e.message : 'Unexpected error', 'NETWORK_ERROR');
		} finally {
			form.setIsSubmitting(false);
		}
	}, [endorsementId, petitionId, form, utilityContext, track, onSuccess, imageUpload.data.image, imageUpload.uploader]);

	const handleNext = useCallback(() => {
		// Form validation is handled by React Hook Form in the Form component
		// No need for duplicate validation here
		setIsSubmitModalOpen(true);
	}, []);

	const handleTermsChange = useCallback((checked: boolean) => {
		setIsTermsAgreed(checked);
	}, []);

	const handleSetIsSubmitModalOpen = useCallback((isOpen: boolean) => {
		setIsSubmitModalOpen(isOpen);
		if (!isOpen) {
			setIsTermsAgreed(false);
		}
	}, []);

	return {
		data: {
			formData: form.formData,
			isSubmitting: form.isSubmitting,
			statementCharCount: form.statementCharCount,
			statementMaxLength: form.statementMaxLength,
			error: form.error,
			showValidation: form.showValidation,
			isSubmitModalOpen,
			image: imageUpload.data.image,
			imageCrop: imageUpload.data.imageCrop,
			imageUploadErrorMessage: imageUpload.data.errorMessage,
			isTermsAgreed,
		},
		actions: {
			handleFieldChange: form.handleFieldChange,
			handlePhotoUpload: form.handlePhotoUpload,
			handleSubmit,
			handleNext,
			clearError: form.clearError,
			resetForm: form.resetForm,
			setIsSubmitModalOpen: handleSetIsSubmitModalOpen,
			handleTermsChange,
			onImageCrop: imageUpload.actions.onImageCrop,
		},
		uploader: imageUpload.uploader,
	};
}
